import { useQueryClient } from "@tanstack/react-query";
import useAPI, { useAPIMutation } from "@/services/api/hook";
import { ExtendedUseQueryResult } from "@/services/api/types";
import {
  WebFormFieldMapUpdate,
  WebFormFormUpdate,
  WebFormLeadField,
  WebFormLeadFields,
  WebFormRoutingUpdate,
  WebFormScriptTag,
  WebFormSettings
} from "@/entities/webForm/types";

const SETTINGS_KEY = "webForm/settings";
const SCRIPT_TAG_KEY = "webForm/scriptTag";
const LEAD_FIELDS_KEY = "webForm/settings/leadFields";

// Every route needs the Settings admin permission, so callers pass `enabled`
// off for anyone else rather than letting the request fail with a 403.
export const useWebFormSettings = (
  enabled = true
): ExtendedUseQueryResult<WebFormSettings> =>
  useAPI(SETTINGS_KEY, undefined, { enabled });

// The tag answers 409 until the key and the form selector both exist.
export const useWebFormScriptTag = (
  enabled: boolean
): ExtendedUseQueryResult<string> =>
  useAPI(SCRIPT_TAG_KEY, undefined, {
    enabled,
    retry: false,
    select: (response: WebFormScriptTag) => response.scriptTag
  });

// The lead fields a submission may fill, by the same rule intake applies.
export const useWebFormLeadFields = (): ExtendedUseQueryResult<
  WebFormLeadField[]
> =>
  useAPI(LEAD_FIELDS_KEY, undefined, {
    select: (response: WebFormLeadFields) => response.fields
  });

// Every save can change the settings and the tag, so both are refetched.
// Returning the refetch keeps the save pending until the saved state shows.
const useRefetchWebForm = () => {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: [SETTINGS_KEY] }),
      queryClient.invalidateQueries({ queryKey: [SCRIPT_TAG_KEY] })
    ]);
};

export const useSaveWebFormStatus = () =>
  useAPIMutation<{ data: { enabled: boolean } }, WebFormSettings>(
    "webForm/settings/status",
    { method: "PUT" },
    useRefetchWebForm()
  );

export const useSaveWebFormForm = () =>
  useAPIMutation<{ data: WebFormFormUpdate }, WebFormSettings>(
    "webForm/settings/form",
    { method: "PUT" },
    useRefetchWebForm()
  );

export const useSaveWebFormRouting = () =>
  useAPIMutation<{ data: WebFormRoutingUpdate }, WebFormSettings>(
    "webForm/settings/routing",
    { method: "PUT" },
    useRefetchWebForm()
  );

export const useSaveWebFormFieldMap = () =>
  useAPIMutation<{ data: WebFormFieldMapUpdate }, WebFormSettings>(
    "webForm/settings/fieldMap",
    { method: "PUT" },
    useRefetchWebForm()
  );

export const useRotateWebFormKey = () =>
  useAPIMutation<Record<string, never>, WebFormSettings>(
    "webForm/settings/rotateKey",
    { method: "POST" },
    useRefetchWebForm()
  );

interface RefusedRequest {
  response?: { data?: { detail?: unknown } };
}

interface ValidationIssue {
  msg?: unknown;
}

// A refused save (a bad origin) answers with FastAPI's validation list, where
// pydantic prefixes each message with "Value error, ".
export const webFormErrorDetail = (error: unknown): string | undefined => {
  const detail = (error as RefusedRequest | null)?.response?.data?.detail;
  if (typeof detail === "string" && detail) return detail;
  if (!Array.isArray(detail)) return undefined;
  const messages = detail
    .map((issue: ValidationIssue | null) => issue?.msg)
    .filter(
      (message): message is string =>
        typeof message === "string" && message !== ""
    )
    .map((message) => message.replace(/^Value error, /, ""));
  return messages.length ? messages.join("; ") : undefined;
};
